// GeexSegmentedBar — Geex frame 26 (workspace/design-refs/geex/
// 26-analytics-segmented-progress.png, "New iOS Users"): one full-width
// segmented bar (solid ink lead segment, tonal steps for the rest) with
// per-segment giant numerals + a square legend marker + name + delta on
// one baseline each. Generalizes the inline composition already shipped
// at workspace/packages/workspace/pages/main.js ("Geex frame 26" —
// read-only, do not edit; this primitive lets OTHER surfaces reuse the
// same visual language via data props instead of copy-pasting markup).
//
// items: [{ label, value, delta? }] — value is the raw magnitude
// (rendered via String()); delta is a plain number, formatted as a signed
// percent ('+1.12%' / '-1.3%'). Segment widths are each item's share of
// the total. First item is the "lead" segment (solid ink); the rest step
// down in tone.
// ink: base foreground token (default 'title' — the adaptive semantic so
// this reads correctly on any neutral surface out of the box). On a hue
// poster, pass the surface's binary ink ('black'/'white') explicitly.
// hue: optional — when set, non-lead segments/markers shade the SAME hue
// (poster-tonal steps, `hue+15`/`hue-15`) instead of ink-alpha. Omit for a
// neutral surface (ink-alpha steps only).

// FA204 / frank trap — helpers live in `scope`, NOT module scope: a bare
// module-scope const silently reads as undefined once the component is
// frank.toJSON'd (children factories then throw and the whole subtree never
// mounts). el.scope.<name> resolves from any descendant handler.
export const GeexSegmentedBar = {
  scope: {
    segTone: (ink, hue, i) => {
      if (i === 0) return ink
      return hue ? (i % 2 ? `${hue}-15` : `${hue}+15`) : i % 2 ? `${ink}.35` : `${ink}.15`
    },
    fmtDelta: (n) => {
      const v = Number(n) || 0
      return `${v > 0 ? '+' : ''}${v}%`
    }
  },
  flow: 'y',
  gap: 'B',
  width: '100%',
  items: [],
  ink: 'title',

  Bar: {
    flow: 'x',
    width: '100%',
    height: 'Z2',
    round: 'radiusPill',
    overflow: 'hidden',
    background: (el) => `${el.parent.ink || 'title'}.12`,
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const items = parent.items || []
      const total = items.reduce((n, it) => n + Math.max(Number(it.value) || 0, 0), 0) || 1
      return items.map((it, i) => ({
        key: it.label || `seg${i}`,
        segFlex: Math.max(Number(it.value) || 0, 0) / total,
        segColor: el.scope.segTone(parent.ink || 'title', parent.hue, i)
      }))
    },
    childExtends: {
      transition: 'A defaultBezier flex',
      // Floor only nonzero shares — a floored sliver appended to a ~full
      // bar reads as an artifact nub; a zero segment should just vanish.
      flex: (el, s) => String(s.segFlex > 0 ? Math.max(s.segFlex, 0.02) : 0),
      background: (el, s) => s.segColor
    }
  },

  Rows: {
    flow: 'y',
    gap: 'D',
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const ink = parent.ink || 'title'
      const hue = parent.hue
      return (parent.items || []).map((it, i) => ({
        key: it.label || `row${i}`,
        label: it.label || '',
        value: it.value,
        delta: it.delta,
        markColor: el.scope.segTone(ink, hue, i),
        ink
      }))
    },
    childExtends: {
      flow: 'y',
      gap: 'X',

      Num: {
        tag: 'span',
        fontSize: 'F',
        fontWeight: '700',
        fontFamily: 'Code',
        lineHeight: '0.95',
        color: (el, s) => s.ink,
        text: (el, s) => String(s.value ?? 0)
      },

      LegendRow: {
        flow: 'x',
        align: 'baseline space-between',
        gap: 'Y',

        Left: {
          flow: 'x',
          align: 'center start',
          gap: 'Y',
          Mark: {
            width: 'X2',
            height: 'X2',
            flexShrink: '0',
            background: (el, s) => s.markColor
          },
          Name: {
            tag: 'span',
            fontSize: 'Z',
            opacity: '0.55',
            color: (el, s) => s.ink,
            // `items[].label` is a KEY-IN-STATE carrier: a consumer may hand this
            // chart a bundle key (workspace/pages/admin/usage.js feeds
            // `admin.usage.attr.systemEmpty`). Resolved HERE, at the render site —
            // `translate()` returns its input unchanged when nothing resolves, so a
            // tenant-supplied label passes through untouched.
            text: (el, s) => el.call('polyglot', s.label || '')
          }
        },

        Delta: {
          tag: 'span',
          fontSize: 'Z',
          fontWeight: '700',
          fontFamily: 'Code',
          color: (el, s) => s.ink,
          hide: (el, s) => s.delta === undefined || s.delta === null,
          text: (el, s) => el.scope.fmtDelta(s.delta)
        }
      }
    }
  }
}
